import { useEffect, useRef, useState } from "react";
import { Plus, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { slug } from "@/lib/geo";

const norm = (s) => slug(s).replace(/-/g, " ");

const distance = (a, b) => {
  const row = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    let prev = row[0];
    row[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const tmp = row[j];
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1));
      prev = tmp;
    }
  }
  return row[b.length];
};
// Similarity against the whole label or its start, so "swizerland" and "swiz" both find Switzerland
const similar = (n, label) => {
  const l = norm(label);
  const whole = 1 - distance(n, l) / Math.max(n.length, l.length);
  const start = 1 - distance(n, l.slice(0, n.length)) / n.length;
  return Math.max(whole, n.length >= 4 ? start - 0.05 : 0);
};

// Type to filter a list (plus optional remote results); offers "Add …" when nothing matches exactly
export const SearchPick = ({ options, remote, onPick, onCreate, createLabel, placeholder, testId, accent = "#34D399" }) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [extra, setExtra] = useState([]);
  const [active, setActive] = useState(0);
  const remoteRef = useRef(remote);
  remoteRef.current = remote;

  useEffect(() => {
    if (!remoteRef.current || q.trim().length < 2) { setExtra([]); return; }
    const t = setTimeout(() => remoteRef.current(q.trim()).then(setExtra).catch(() => setExtra([])), 200);
    return () => clearTimeout(t);
  }, [q]);

  const n = norm(q.trim());
  let local = options.filter((o) => !n || norm(o.label).includes(n)).slice(0, 8);
  if (n.length >= 3 && local.length < 3) {
    const seen = new Set(local.map((o) => o.key));
    const close = options.filter((o) => !seen.has(o.key)).map((o) => [o, similar(n, o.label)]).filter(([, sc]) => sc >= 0.7)
      .sort((a, c) => c[1] - a[1]).slice(0, 5).map(([o]) => ({ ...o, hint: "Did you mean?", hintColor: "#7DD3FC" }));
    local = [...local, ...close];
  }
  const keys = new Set(local.map((o) => o.key));
  const items = [...local, ...extra.filter((o) => !keys.has(o.key))].slice(0, 10);
  const exact = items.some((o) => norm(o.label) === n || norm(o.value || "") === n);
  const rows = onCreate && n && !exact ? [...items, { key: "__create", create: true, label: q.trim() }] : items;

  const choose = (o) => {
    if (o.create) onCreate(o.label); else onPick(o);
    setQ(""); setOpen(false); setActive(0);
  };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, rows.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (rows[active]) choose(rows[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
      <Input value={q} placeholder={placeholder} className="pl-8" data-testid={testId}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} onKeyDown={onKey} />
      {open && rows.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-lg border border-white/10 bg-[#131A26] shadow-xl py-1" data-testid={`${testId}-options`}>
          {rows.map((o, i) => (
            <li key={o.key}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); choose(o); }} onMouseEnter={() => setActive(i)}
                data-testid={o.create ? `${testId}-create` : `${testId}-option-${slug(o.key)}`}
                className={`w-full text-left px-3 py-1.5 text-sm flex items-center justify-between gap-3 ${i === active ? "bg-white/10" : ""}`}>
                {o.create ? (
                  <span className="flex items-center gap-1.5" style={{ color: accent }}><Plus size={13} /> {createLabel ? createLabel(o.label) : `Add "${o.label}"`}</span>
                ) : (
                  <>
                    <span className="truncate">{o.label}</span>
                    {o.hint && <span className="shrink-0 text-[10px] font-mono uppercase tracking-wide" style={{ color: o.hintColor || "#64748B" }}>{o.hint}</span>}
                  </>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
